import { useState, useCallback, useEffect, useRef } from 'react';
import useFetch, { muteAll } from 'hooks/useFetch';
import { createCardViewList } from 'utils';
import { getSearchResults } from '../actions';
import { generateFakeSearchResults } from '../utils';
import * as debugConfig from '../debug';
const INITIAL_STATE = {
    loading: true,
    error: false,
    data: null,
    count: 0,
};
function searchOffline(searchTerm, data) {
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexp = new RegExp(escaped, 'i');
    const matches = new Set(data
        .filter((stock) => {
        if (stock.symbol) {
            return stock.name.match(regexp) || stock.symbol.match(regexp) || stock.originalName.match(regexp);
        }
        return stock.name.match(regexp) || stock.originalName.match(regexp);
    })
        .map((stock) => stock.id));
    return data.filter((stock) => matches.has(stock.id));
}
const useStockSearchResults = (shouldCacheAllStocks, broker = 'alpaca', options) => {
    const [state, setState] = useState(INITIAL_STATE);
    const dataRef = useRef(null);
    const allStocksRef = useRef(null);
    const countRef = useRef(0);
    const assetIdSet = useRef(undefined);
    const allAssetIdSet = useRef(undefined);
    const currentSearchTerm = useRef(undefined);
    const canPerformOfflineSearch = useRef(false);
    const currentPage = useRef(0);
    const currentPageForAllStocks = useRef(0);
    const isStillMounted = useRef(true);
    const [, , , fetch] = useFetch(muteAll);
    const brokerRef = useRef(broker);
    const reset = useCallback(() => {
        countRef.current = 0;
        dataRef.current = null;
        currentPage.current = 0;
        allStocksRef.current = null;
        assetIdSet.current = undefined;
        allAssetIdSet.current = undefined;
        currentPageForAllStocks.current = 0;
        currentSearchTerm.current = undefined;
        canPerformOfflineSearch.current = false;
        setState(INITIAL_STATE);
    }, []);
    useEffect(() => {
        if (brokerRef.current !== broker) {
            brokerRef.current = broker;
            reset();
        }
    }, [broker, reset]);
    const search = useCallback(async (searchTerm) => {
        // stop pagination and reset if shouldCacheAllStocks is true and searchTerm is empty or undefined
        if (!shouldCacheAllStocks && !searchTerm) {
            currentSearchTerm.current = undefined;
            reset();
            return;
        }
        const isNextPageRequest = typeof searchTerm === 'object' && 'distanceFromEnd' in searchTerm;
        const searchTermDidNotChange = searchTerm === currentSearchTerm.current ||
            (isNextPageRequest && (currentSearchTerm.current !== undefined || shouldCacheAllStocks));
        if (canPerformOfflineSearch.current && allStocksRef.current && allStocksRef.current.length) {
            if (typeof searchTerm === 'string') {
                currentSearchTerm.current = searchTerm;
                const results = searchOffline(searchTerm, allStocksRef.current);
                const finalResults = createCardViewList(results);
                setState({ loading: false, error: false, data: finalResults, count: countRef.current });
            }
            return;
        }
        let page;
        if (searchTermDidNotChange) {
            // fetch next page
            currentPage.current += 1;
            if (!searchTerm || (typeof searchTerm === 'object' && !currentSearchTerm.current)) {
                currentPageForAllStocks.current += 1;
                page = currentPageForAllStocks.current; // the page we will be fetching in this request
            }
            else {
                page = currentPage.current; // the page we will be fetching in this request
            }
        }
        else {
            /**
             * Search term changed, start from page 1 and reset data,
             * unless the search term became empty while we already have some cache.
             * In that case, we need to initialize the new search results with the cache that we already have
             */
            if (!searchTerm) {
                currentPage.current = currentPageForAllStocks.current + 1;
                if (allStocksRef.current !== null) {
                    dataRef.current = [...allStocksRef.current];
                    assetIdSet.current = new Set(allAssetIdSet.current);
                    const finalResults = createCardViewList(allStocksRef.current);
                    setState({
                        error: false,
                        loading: false,
                        count: countRef.current,
                        data: finalResults,
                    });
                }
                else {
                    currentPage.current = 1;
                    currentPageForAllStocks.current = 1;
                    dataRef.current = [];
                    allStocksRef.current = [];
                    assetIdSet.current = undefined;
                    allAssetIdSet.current = undefined;
                }
            }
            else {
                currentPage.current = 1;
                dataRef.current = [];
                assetIdSet.current = undefined;
            }
            page = currentPage.current; // the page we will be fetching in this request
            if (typeof searchTerm === 'string' || typeof searchTerm === 'undefined') {
                currentSearchTerm.current = searchTerm;
            }
        }
        const isLoadingNextPage = typeof searchTerm !== 'string';
        const searchTermIsEmpty = currentSearchTerm.current === undefined;
        const isCachingInactiveSearch = searchTermIsEmpty && isLoadingNextPage;
        if (!isCachingInactiveSearch || shouldCacheAllStocks) {
            const validSearchTerm = currentSearchTerm.current;
            try {
                let typeQueryParams;
                if (options?.categoryId) {
                    typeQueryParams = { categoryId: options?.categoryId };
                }
                if (options?.collectionId) {
                    if (typeQueryParams) {
                        typeQueryParams.collectionId = options.collectionId;
                    }
                    else {
                        typeQueryParams = { collectionId: options.collectionId };
                    }
                }
                const response = debugConfig.useFakeData
                    ? generateFakeSearchResults(validSearchTerm, currentPage.current)
                    : await fetch(getSearchResults(broker, currentSearchTerm.current, currentPage.current, typeQueryParams));
                if (validSearchTerm === currentSearchTerm.current && isStillMounted.current) {
                    // if search term did change then this request is already old and irrelevant
                    if (response?.assets) {
                        if (page !== 1 && response.assets.length === 0) {
                            // no results, but it’s not page 1, so do not reset data
                            if (!validSearchTerm) {
                                // we are done caching all stocks, now we can perform offline search
                                canPerformOfflineSearch.current = true;
                            }
                        }
                        else {
                            if (!assetIdSet.current) {
                                dataRef.current = [];
                                assetIdSet.current = new Set();
                            }
                            if (!dataRef.current) {
                                dataRef.current = [];
                                assetIdSet.current = new Set();
                            }
                            if (validSearchTerm === '' || validSearchTerm === undefined) {
                                if (!allAssetIdSet.current) {
                                    allAssetIdSet.current = new Set();
                                }
                                if (!allStocksRef.current) {
                                    allStocksRef.current = [];
                                }
                                for (let i = 0; i < response.assets.length; i += 1) {
                                    const { id } = response.assets[i];
                                    if (validSearchTerm === '' || validSearchTerm === undefined) {
                                        if (!allAssetIdSet.current.has(id)) {
                                            const { icon, ...asset } = response.assets[i];
                                            if (icon) {
                                                allAssetIdSet.current.add(id);
                                                allStocksRef.current.push({ ...asset, iconUrl: icon });
                                            }
                                        }
                                    }
                                }
                            }
                            for (let i = 0; i < response.assets.length; i += 1) {
                                const { id } = response.assets[i];
                                if (!assetIdSet.current.has(id)) {
                                    const { icon, ...asset } = response.assets[i];
                                    if (icon) {
                                        assetIdSet.current.add(id);
                                        dataRef.current.push({ ...asset, iconUrl: icon });
                                    }
                                }
                            }
                            countRef.current = response.count;
                            const finalResults = createCardViewList(dataRef.current);
                            setState({
                                error: false,
                                loading: false,
                                data: finalResults,
                                count: response.count,
                            });
                            if (response.assets.length > 0 && debugConfig.LOAD_NEXT_PAGE_RIGHT_AWAY) {
                                search({ distanceFromEnd: 0 }); // fetch next page right away
                            }
                        }
                    }
                    else {
                        const finalResults = createCardViewList(dataRef.current);
                        setState({ loading: false, error: true, data: finalResults, count: countRef.current });
                    }
                }
            }
            catch {
                if (validSearchTerm === currentSearchTerm.current) {
                    const finalResults = createCardViewList(dataRef.current);
                    setState({ loading: false, error: true, data: finalResults, count: countRef.current });
                }
            }
        }
    }, [shouldCacheAllStocks, reset, options?.categoryId, options?.collectionId, broker]);
    const loadMore = useCallback(() => {
        search({ distanceFromEnd: 0 });
    }, [search]);
    useEffect(() => {
        search();
    }, [search]);
    useEffect(() => () => {
        isStillMounted.current = false;
    }, []);
    return [state.data, search, state.loading, canPerformOfflineSearch.current, state.count, loadMore];
};
export default useStockSearchResults;
